import {
  SecurityAdvice,
  SecurityThreat,
  SecurityThreatTrend,
  translateThreatLevelToColour,
  translateThreatLevelToText,
  translateThreatTrendToColor,
  translateThreatTrendToText,
} from '../../../models/pro.model';
import {
  cellBaseStyle,
  fullWidthCellStyle,
  quarterWidthCellStyle,
  tableHeaderColour,
  tableHeaderTextColour,
  tableSubHeaderColour,
  tableWidth,
} from '../pro-report-email-styles';
import {
  ProReportEmailFullWidthInfoRow,
  ProReportEmailFullWidthTableHeader,
  Spacer,
} from './pro-report-email-common-components';

/* Email-specific method for best visibility */
const translateThreatTrendToIcon = (threatTrend: SecurityThreatTrend) => {
  switch (threatTrend) {
    case SecurityThreatTrend.UNCHANGED:
      return '⇒';
    case SecurityThreatTrend.UP:
      return '⇗';
    case SecurityThreatTrend.DOWN:
      return '⇘';
    default:
      return '';
  }
};

function ProReportEmailSecurityThreatTableHeader() {
  return (
    <tr
      style={{
        backgroundColor: tableHeaderColour,
        color: tableHeaderTextColour,
        fontWeight: 'bold',
      }}
    >
      <th
        style={{
          ...cellBaseStyle,
        }}
      >
        Threat
      </th>
      <th
        style={{
          ...quarterWidthCellStyle,
        }}
      >
        Level
      </th>
      <th
        style={{
          ...quarterWidthCellStyle,
        }}
      >
        Trend
      </th>
    </tr>
  );
}

function ProReportEmailSecurityThreatTableRow({
  threat,
}: {
  threat: SecurityThreat;
}) {
  const { type, level, trend } = threat;
  return (
    <tr
      style={{
        ...cellBaseStyle,
      }}
    >
      <td
        style={{
          ...cellBaseStyle,
        }}
      >
        {type}
      </td>
      <td
        style={{
          ...quarterWidthCellStyle,
          fontWeight: 'bold',
          color: translateThreatLevelToColour(level),
        }}
      >
        {translateThreatLevelToText(level)}
      </td>
      <td
        style={{
          ...quarterWidthCellStyle,
          color: translateThreatTrendToColor(trend),
        }}
      >
        {translateThreatTrendToText(trend)} {translateThreatTrendToIcon(trend)}
      </td>
    </tr>
  );
}

function ProReportSecurityThreatTable({
  threatTableData,
}: {
  threatTableData: SecurityThreat[];
}) {
  if (threatTableData && threatTableData.length > 0) {
    return (
      <table
        width={tableWidth}
        cellPadding={0}
        cellSpacing={0}
        style={{ borderCollapse: 'collapse', margin: 0, padding: 0 }}
        role="presentation"
      >
        <ProReportEmailSecurityThreatTableHeader />
        {threatTableData.map((row) => (
          <ProReportEmailSecurityThreatTableRow threat={row} />
        ))}
      </table>
    );
  }
  return null;
}

function ProReportEmailSecurityAdvice({
  securityAdvice,
}: {
  securityAdvice: SecurityAdvice | null;
}) {
  const headerText = 'Security advice';
  if (securityAdvice) {
    return (
      <>
        <table
          width={tableWidth}
          cellPadding={0}
          cellSpacing={0}
          style={{ borderCollapse: 'collapse', margin: 0, padding: 0 }}
          role="presentation"
        >
          <ProReportEmailFullWidthTableHeader headerText={headerText} />
          <ProReportEmailFullWidthInfoRow
            heading="Source:"
            content={securityAdvice.source}
            backgroundColor={tableSubHeaderColour}
          />
          <tr>
            <td
              colSpan={2}
              style={{
                ...fullWidthCellStyle,
              }}
            >
              <strong>Summary:</strong>
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: securityAdvice.summary }}
              />
            </td>
          </tr>
        </table>
        {securityAdvice.threats.length > 0 && (
          <>
            <Spacer />
            <ProReportSecurityThreatTable
              threatTableData={securityAdvice.threats}
            />
          </>
        )}
        <Spacer />
      </>
    );
  }
  return null;
}

export default ProReportEmailSecurityAdvice;
